import {Component, OnInit, inject} from '@angular/core';
import {catchError, of} from 'rxjs';
import {filter} from 'rxjs/operators';
import {Router, NavigationEnd} from '@angular/router';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {PracticeSelectionFormComponent, SupportInfoService} from '@wellfit/wf-plans-services';
import {EventsManager} from './events/events.manager';
import {PlansService} from './services/plans.service';
import {DataService} from './services/data.service';
import {SupportInformation} from './models/SupportInformation.model';
import {environment} from '../environments/environment';
import {LegalModel} from './models/legal.model';
import {ErrorService} from './services/error.service';
import {MainPageDetailModel} from './models/plans.model';
import {ThemeService} from './services/theme.service';
import {BrandRequest} from './models/requests/brand.request';
import {Theme, themes} from './models/Theme';
import {IThemeConfig} from './models/theme.entity';
import {DOCUMENT} from '@angular/common';
import {adjustLightness} from './utils/color.util';
import {BrandThemeResponse} from './models/requests/brand-theme.response';
import {isStaticRoute} from './core/constants/routing.constants';
import {RuntimeOrganizationService} from './core/services/runtime-organization.service';
import {ResolveResultKindEnum} from './enums/resolve-result-kind.enum';
import {ResolveResultType} from './types/resolve-result.type';
import {SeoService} from './core/services/seo.service';
import {AnalyticsService, SMILE_HEALTH_MASTER_GOOGLE_ANALYTICS_ID} from './core/services/analytics.service';
import {PLACEHOLDER_ORGANIZATION_ID} from './core/constants/organization.constants';
import {PURCHASE_WIDGET_STYLES_BUNDLE_ID} from './app.constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})

export class AppComponent implements OnInit {
  private _runtimeOrganizationService: RuntimeOrganizationService = inject(RuntimeOrganizationService);
  private _supportInfoService: SupportInfoService = inject(SupportInfoService);
  private _analyticsService: AnalyticsService = inject(AnalyticsService);
  private _eventsManager: EventsManager = inject(EventsManager);
  private _themeService: ThemeService = inject(ThemeService);
  private _plansService: PlansService = inject(PlansService);
  private _errorService: ErrorService = inject(ErrorService);
  private _dataService: DataService = inject(DataService);
  private _seoService: SeoService = inject(SeoService);
  private _modalService: NgbModal = inject(NgbModal);
  private _document: Document = inject(DOCUMENT);
  private _router: Router = inject(Router);

  public office?: MainPageDetailModel = undefined;
  public geoSaved: boolean = false;
  public showSubHeader: boolean = true;
  public legal: LegalModel[] = [];
  public adminBy: string = '';

  private _isMasterOrigin: boolean = true;

  public async ngOnInit() {
    const location = this._document?.location;
    if (!location) {
      return;
    }

    const pathname = location.pathname;
    const args = pathname.split('/');

    this._eventsManager.orgId = PLACEHOLDER_ORGANIZATION_ID;

    const resolveResult: ResolveResultType = await this._resolveOrigin(location.origin);

    if (resolveResult.kind === ResolveResultKindEnum.Org) {
      this._isMasterOrigin = false;
      this._eventsManager.orgId = resolveResult.context.organizationId;
      this._dispatchOnCustomDomainOrigin(resolveResult.context.vanityName, pathname, args);
    } else {
      this._isMasterOrigin = true;
      this._analyticsService.initializeGoogleAnalytics(SMILE_HEALTH_MASTER_GOOGLE_ANALYTICS_ID);
      this._dispatchOnMasterOrigin(pathname, args);
    }

    this._eventsManager.showHideSubheader
      .subscribe((data: boolean) =>{
        this.showSubHeader = data;
      });

    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url: string = (event as NavigationEnd).urlAfterRedirects;
        if (url.startsWith('/ancillary-search')) {
          this._modalService.dismissAll();
        }
        this._toggleWidgetStyles(url);
      });

    this._toggleWidgetStyles(this._router.url);

    this._addClarity();
  }

  private async _resolveOrigin(origin: string): Promise<ResolveResultType> {
    const cached = this._runtimeOrganizationService.getCached(origin);
    if (cached) {
      return {kind: ResolveResultKindEnum.Org, context: cached};
    }
    return await this._runtimeOrganizationService.resolveFromBff(origin);
  }

  private _dispatchOnMasterOrigin(pathname: string, args: string[]): void {
    if (!this.geoSaved && pathname === '/') {
      this._openGeoModal();
    } else if (args[1] === 'office' && args[2]) {
      this._getOffice(args[2]);
    } else if (args[1] && !isStaticRoute(args[1])) {
      if (args[2]) {
        this._getOfficeByVanity(args[1], args[2]);
      } else {
        this._getOfficeByVanity('', args[1]);
      }
    } else if (sessionStorage['office']) {
      const office: MainPageDetailModel = JSON.parse(sessionStorage['office']);
      this._getOffice(office.practiceId);
    }
  }

  private _dispatchOnCustomDomainOrigin(vanityName: string, pathname: string, args: string[]): void {
    if (!this.geoSaved && pathname === '/') {
      this._getOfficeByVanity(vanityName, '');
    } else if (args[1] === 'office' && args[2]) {
      this._getOffice(args[2]);
    } else if (args[1] && !isStaticRoute(args[1])) {
      this._getOfficeByVanity(vanityName, args[1]);
    } else if (sessionStorage['office']) {
      const off = JSON.parse(sessionStorage['office']);
      this._getOffice(off.practiceId);
    }
  }

  private _buildCustomDomainRedirect(args: string[], office: MainPageDetailModel): string {
    const base = `https://${office.organizationCustomWebsiteUrl}`;

    if (args[2]) {
      return `${base}/${args[2]}`;
    }
    if (args[1].toLowerCase() === office.vanityName.toLowerCase()) {
      return `${base}/`;
    }
    return `${base}/${args[1]}`;
  }

  private _shouldRedirectToCustomDomain(office: MainPageDetailModel): boolean {
    if (!office.organizationCustomWebsiteActive || !office.organizationCustomWebsiteUrl) {
      return false;
    }
    return this._isMasterOrigin;
  }

  private _toggleWidgetStyles(url: string): void {
    if (url.startsWith('/purchase')) {
      this._addWidgetStyles();
    } else {
      this._removeWidgetStyles();
    }
  }

  private _addWidgetStyles(): void {
    if (this._document.getElementById(PURCHASE_WIDGET_STYLES_BUNDLE_ID)) {
      return;
    }
    const link: HTMLLinkElement = this._document.createElement('link');
    link.id = PURCHASE_WIDGET_STYLES_BUNDLE_ID;
    link.rel = 'stylesheet';
    link.href = `/${PURCHASE_WIDGET_STYLES_BUNDLE_ID}.css`;
    this._document.head.appendChild(link);
  }

  private _removeWidgetStyles(): void {
    this._document.getElementById(PURCHASE_WIDGET_STYLES_BUNDLE_ID)?.remove();
  }

  private _openGeoModal(): void {
    const modalRef: NgbModalRef = this._modalService.open(PracticeSelectionFormComponent);
    modalRef.componentInstance.orgId = this._eventsManager.orgId;
    modalRef.componentInstance.showMap = environment.production ? true : !!environment.googleMapsApiKey;
    modalRef.componentInstance.officeSelected.subscribe((officeId: string): void => {
      this._router.navigate(['/office', officeId]);
      this._getOffice(officeId);
      this._closeModal();
    });
    modalRef.componentInstance.closeWidget.subscribe((): void => {
      this._closeModal();
    });
  }

  private _closeModal(): void {
    this._modalService.dismissAll();
  }

  private _getOffice(office: string): void {
    this._plansService.getOffice(office).subscribe((result: MainPageDetailModel): void => {
      this.office = result;
      this.adminBy = this._getAdminBy(this.office.benefitPlanTemplates?.[0]?.isAdminBy);
      this._eventsManager.orgId = this._eventsManager.docOrgId = this.office.organizationId;
      this.showSubHeader = true;
      this._eventsManager.subHeaderShowHide(true);
      this._getLegalLanguage();
      this._eventsManager.setOfficeInfo(this.office);
      this.setSupportInfo(this.office);
      this._getTheme();
      this._updateSeoForOffice(this.office);
      this._initializeAnalyticsForOffice(this.office);
    }, (error): void => {
      if (error.status === 400 && Array.isArray(error.error)) {
        this._processValidationError(error.error);
      }
    });
  }

  private _updateSeoForOffice(office: MainPageDetailModel): void {
    this._seoService.setPage(office.name, `Dental membership plans at ${office.name}`);
  }

  private _initializeAnalyticsForOffice({googleAnalyticsId}: MainPageDetailModel): void {
    const measurementId: string = googleAnalyticsId || SMILE_HEALTH_MASTER_GOOGLE_ANALYTICS_ID;
    this._analyticsService.initializeGoogleAnalytics(measurementId);
  }

  private _processValidationError(validationErrors: {message: string}[]): void {
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      this._errorService.showError(validationErrors[0].message);
    }
  }

  private _getOfficeByVanity(orgVanity: string, practiceVanity: string): void {
    this._plansService.getOfficeByVanityName(orgVanity, practiceVanity).subscribe((result: MainPageDetailModel): void => {
      if (this._shouldRedirectToCustomDomain(result)) {
        const args: string[] = this._document.location.pathname.split('/');
        window.location.replace(this._buildCustomDomainRedirect(args, result));
        return;
      }

      this.office = result;
      this.adminBy = this._getAdminBy(this.office?.benefitPlanTemplates?.[0]?.isAdminBy);
      this._eventsManager.orgId = this._eventsManager.docOrgId = this.office?.organizationId ?? '';
      this.showSubHeader = true;
      this._eventsManager.subHeaderShowHide(true);
      this._getLegalLanguage();
      this._eventsManager.setOfficeInfo(this.office);
      this.setSupportInfo(this.office);
      this._getTheme();
      this._updateSeoForOffice(this.office);
      this._initializeAnalyticsForOffice(this.office);

      if (this.office?.practiceId == null) {
        this._openGeoModal();
      }
    });
  }

  public setSupportInfo(office: MainPageDetailModel): void {
    const fallback: SupportInformation = {
      PhoneNumber: office?.telephoneNumber ?? '',
      MailingAddress: office?.address ?? '',
      EmailAddress: office?.emailSupport ?? '',
      WeekdayHours: office?.weekdayHours ?? '',
      WeekendHours: office?.weekendHours ?? '',
      ProfileName: ''
    };
    this._dataService.setSupportInfo(fallback);

    const templateId: string = office?.benefitPlanTemplates?.[0]?.benefitPlanTemplateId;
    if (templateId) {
      this._supportInfoService.getSupportInfo(templateId).pipe(
        catchError(() => of(null))
      ).subscribe(info => {
        if (info) {
          this._dataService.setSupportInfo({
            PhoneNumber: info.telephoneNumber,
            EmailAddress: info.emailSupport,
            MailingAddress: info.address,
            WeekdayHours: info.weekdayHours,
            WeekendHours: info.weekendHours,
            ProfileName: info.profileName
          });
        }
      });
    }
  }

  private _getLegalLanguage(): void {
    this._plansService.getLegalLanguage().subscribe((result: LegalModel[]): void => {
      this.legal = result;
      if (this.adminBy == '') {
        this.adminBy = 'wellfit';
      }
      this._eventsManager.setLegalLanguage(this.legal?.find?.((item: LegalModel): boolean => item.adminBy === this.adminBy));
    });
  }

  private _getTheme(): void {
    const theme: BrandRequest = {
      themeId: '',
      brand:this.office?.benefitPlanTemplates?.[0]?.branding ?? 'generic',
      organization: this.office?.organizationId ?? this._eventsManager.orgId,
      practice:this.office?.practiceId || ''
    };
    this._themeService.getTheme(theme).subscribe((result: BrandThemeResponse): void => {
      if (result && (result.colorPrimary !== '' || result.organizationLogo !== '')) {
        this._setCustomTheme(result);
      } else {
        this._setDefaultTheme();
      }
    });
  }

  private _getAdminBy(who?: string): string {
    if (who?.length == 1) {
      switch (who) {
        case '0': return 'wellfit';
        case '1': return 'careington';
        default: return 'wellfit';
      }
    } else {
      return this.office?.benefitPlanTemplates?.[0]?.isAdminBy ?? 'wellfit';
    }
  }

  private _setDefaultTheme(): void {
    Theme.applyTheme(themes.default, this._document);
    this._eventsManager.setNewCustTheme.emit({
      ...themes.default.themeProperties as unknown as IThemeConfig,
      bannerDesignType: '',
      bannerImageUrl: '',
      bannerImageMobileUrl: '',
      bannerImageAltText: ''
    });
  }

  private _setCustomTheme(theme: BrandThemeResponse): void {
    if (theme.colorPrimary || theme.organizationLogo) {
      const defaultPrimary: string = themes.default.themeProperties['--color-primary'];
      const customThemeConfig: IThemeConfig = {
        primary: theme.colorPrimary || defaultPrimary,
        primary80: theme.colorPrimary80 || adjustLightness(theme.colorPrimary || defaultPrimary, -25),
        primary60: theme.colorPrimary60 || adjustLightness(theme.colorPrimary || defaultPrimary, -15),
        primary20: adjustLightness(theme.colorPrimary || defaultPrimary, 40) || themes.default.themeProperties['--color-primary20'],
        onPrimary: theme.colorsOnPrimary || themes.default.themeProperties['--color-on-primary'],
        surface: theme.colorSurface || themes.default.themeProperties['--color-surface'],
        onSurface: theme.colorOnSurface || themes.default.themeProperties['--color-on-surface'],
        surfaceAvatar: theme.colorSurfaceAvatar || themes.default.themeProperties['--color-surface-avatar'],
        onSurfaceAvatar: theme.colorOnSurfaceAvatar || themes.default.themeProperties['--color-on-surface-avatar'],
        surfaceVariant: theme.colorSurfaceVariant || themes.default.themeProperties['--color-surface-variant'],
        selectedGroup: theme.colorSelectedGroup || themes.default.themeProperties['--color-selected-group'],
        selectedItem: theme.colorSelectedItem || themes.default.themeProperties['--color-selected-item'],
        selectedItemHover: theme.colorSelectedItemHover || themes.default.themeProperties['--color-selected-item-hover'],
        onSelectedItem: theme.colorOnSelectedItem || themes.default.themeProperties['--color-on-selected-item'],
        surfaceContainer: theme.colorSurfaceContainer || themes.default.themeProperties['--color-surface-container'],
        surfaceLogo: theme.surfaceLogo || themes.default.themeProperties['--color-surface-logo'],
        organizationLogo: this._getOrganizationLogo(theme),
        bannerDesignType: theme.bannerDesignType || '',
        bannerImageUrl: theme.bannerImageUrl || '',
        bannerImageMobileUrl: theme.bannerImageMobileUrl || '',
        bannerImageAltText: theme.bannerImageAltText || 'Dental plan membership'
      };
      this._eventsManager.setNewCustTheme.emit(customThemeConfig);
      this._eventsManager.setThemeId(theme?.id || '');
      Theme.applyTheme(new Theme(customThemeConfig), this._document);
    }
  }

  private _getOrganizationLogo({organizationLogo}: BrandThemeResponse): string {
    return organizationLogo ? `${environment.blobStorageUrl}${organizationLogo}` : '';
  }

  private _addClarity(): void {
    if (!!environment.clarityKey && environment.clarityKey !== '') {
      const script: HTMLScriptElement = document.createElement('script');
      script.innerHTML = `(function(c,l,a,r,i,t,y){ c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}; t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i; y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y); })(window, document, "clarity", "script", "${environment.clarityKey}");`;
      this._document.head.appendChild(script);
    }
  }
}
